import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api/client';

type SubmitStatus = 'idle' | 'submitting' | 'done';

/**
 * Wraps a mutating call with the three things every admin form needs: a busy
 * flag, a message to show, and a callback for when it succeeds. A `501` is
 * reported as "not built yet" rather than as a failure, so scaffolded forms
 * behave predictably while the endpoints are being filled in.
 */
export function useSubmit<TInput, TResult>(
  action: (input: TInput) => Promise<TResult>,
  onSuccess?: (result: TResult) => void,
): {
  submit: (input: TInput) => Promise<void>;
  status: SubmitStatus;
  message: string | null;
  busy: boolean;
} {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const submit = useCallback(
    async (input: TInput) => {
      setStatus('submitting');
      setMessage(null);
      try {
        const result = await action(input);
        setStatus('done');
        onSuccess?.(result);
      } catch (cause) {
        setStatus('idle');
        if (cause instanceof ApiError && cause.isNotImplemented) {
          setMessage('This endpoint is scaffolded but not implemented yet.');
        } else if (cause instanceof ApiError && cause.isDenied) {
          setMessage('Access denied.');
        } else {
          setMessage(cause instanceof Error ? cause.message : 'Something went wrong.');
        }
      }
    },
    [action, onSuccess],
  );

  return { submit, status, message, busy: status === 'submitting' };
}
